import {
  ComponentPropsWithoutRef,
  forwardRef,
  createContext,
  useState,
  useId,
  useMemo,
} from "react";
import cx from "classnames";
import styles from "./Details.module.scss";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil } from "../../hooks";

import { DetailsSummary } from "./DetailsSummary";
import { DetailsArrow } from "./DetailsArrow";
import { DetailsContent } from "./DetailsContent";

export type DetailsContextProps = {
  id: string;
  isExpanded: boolean;
  expanded: () => void;
  close: () => void;
  toggle: () => void;
};

export const DetailsContext = createContext<DetailsContextProps>({
  id: "",
  isExpanded: false,
  expanded: () => undefined,
  close: () => undefined,
  toggle: () => undefined,
});

/**
 * Props for the Details component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type DetailsProps = ComponentPropsWithoutRef<"div"> &
  LayoutUtilProps & {
    /**
     * Determines the controlled expanded state of the details.
     * @remarks Use this to make the details expanded state controlled.
     * @default false
     */
    expanded?: boolean;
    /**
     * Determines the default expanded state of the details.
     * @remarks Use this to make the details expanded state uncontrolled.
     * @default false
     */
    defaultExpanded?: boolean;
  };

const DetailsElement = forwardRef<HTMLDivElement, DetailsProps>(
  function Details(props, ref) {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      children,
      id: idProp,
      expanded: expandedControlled,
      defaultExpanded = false,
      style,
      className,
      ...rest
    } = componentProps;
    const id = useId();
    const [isExpanded, setIsOpen] = useState(defaultExpanded);

    function expanded() {
      setIsOpen(true);
    }

    function close() {
      setIsOpen(false);
    }

    function toggle() {
      setIsOpen((prevIsOpen: boolean) => !prevIsOpen);
    }

    const styleCombined = {
      ...style,
      ...layoutStyles,
    };

    const value = useMemo(
      () => ({
        id: idProp != null ? idProp : id,
        isExpanded:
          expandedControlled != null ? expandedControlled : isExpanded,
        expanded,
        close,
        toggle,
      }),
      [id, idProp, isExpanded, expandedControlled],
    );
    return (
      <DetailsContext.Provider value={value}>
        <div
          data-anv="details"
          ref={ref}
          className={cx(styles.details, className)}
          style={styleCombined}
          {...rest}
        >
          {children}
        </div>
      </DetailsContext.Provider>
    );
  },
);

/**
 * Details component for creating expandable/collapsible content sections.
 *
 * Features:
 * - Expandable and collapsible content sections
 * - Controlled and uncontrolled state management
 * - Accessible with proper ARIA attributes
 * - Smooth animations for expand/collapse
 * - Keyboard navigation support
 * - Layout utility props for positioning and spacing
 * - Compound component pattern with Summary, Content, and Arrow sub-components
 *
 * @example
 * <Details defaultExpanded>
 *   <Details.Summary>Click to expand</Details.Summary>
 *   <Details.Content>This content is now visible</Details.Content>
 * </Details>
 */
export const Details = Object.assign(DetailsElement, {
  /**
   * DetailsSummary component for the clickable header of a Details section.
   *
   * Features:
   * - Clickable button that toggles the Details expanded state
   * - Automatic ARIA attributes for accessibility
   * - Includes animated arrow indicator
   * - Supports custom onClick handlers
   * - Keyboard navigation support (Enter, Space)
   * - Styled with consistent typography and spacing
   *
   * @example
   * <Details.Summary onClick={() => console.log('Summary clicked')}>
   *   Click to expand content
   * </Details.Summary>
   */
  Summary: DetailsSummary,
  /**
   * DetailsContent component for the expandable content area of a Details section.
   *
   * Features:
   * - Expandable and collapsible content area
   * - Smooth animations for height transitions
   * - Configurable height and maxHeight constraints
   * - Automatic overflow handling when height is constrained
   * - Proper ARIA associations with the summary
   * - Responsive design with flexible layout
   *
   * @example
   * <Details.Content maxHeight={300}>
   *   <p>This content will be constrained to 300px max height</p>
   * </Details.Content>
   */
  Content: DetailsContent,
  /**
   * DetailsArrow component for the animated arrow indicator in DetailsSummary.
   *
   * Features:
   * - Animated chevron icon that rotates on expand/collapse
   * - Automatically reflects the current expanded state
   * - Uses consistent icon sizing and styling
   * - Smooth transition animations
   * - Accessible with proper ARIA context
   *
   * @example
   * <DetailsArrow className="custom-arrow" />
   */
  Arrow: DetailsArrow,
});
